/**
 * THE TAKEN MESSAGE — plan-018 DOM (design §30.5, §30.6.17, §30.6.18; ADR 0060; owner answers Q4, Q6; OD-4; D3).
 *
 * One structured message a caller left for the business: a name, the message and one way to reach them, read back once
 * and confirmed. It is recorded by the platform tool `take_message`, handled in-process by both hosts (never sent to
 * the allowlist), as a `taken_message` row (migration 0144) plus an outbox message in one db_transaction; the worker
 * turns it into one `taken_message_notice` email per workspace Owner and Admin.
 *
 * This file is the pure part both hosts share — the closed lists, the limits, and {@link takeMessageVerdictOf}, which
 * turns the model's arguments plus what the conversation knows into an accepted message or one refusal code (`why`)
 * the model reads and acts on:
 *
 * ```
 * shape                      not an object / a field of the wrong type / an unknown contact_kind  → arguments_malformed
 * confirmed ≠ true                                                                                → read_back_first
 * name trimmed, 1..120                                                                            → name_missing
 * message trimmed, 1..2000                                                                        → message_missing
 * calling_number  no CALLER NUMBER (withheld, browser, chat)          → no_calling_number_ask_for_a_number
 *                 the conversation's own number, verified — never a number the model wrote
 * phone           libphonenumber-js (max metadata) under the region the conversation reached; no region: "+…" only
 *                 invalid → phone_not_valid_ask_again; value E.164
 *                 typed:  its national digits appear in one of the visitor's typed turns, else
 *                         contact_not_typed_ask_visitor_to_type_it
 *                 spoken: verified when its national digits appear in the caller's recognised turns (digits joined)
 * email           ≤ 254, one "@", a dotted domain, no spaces → else email_not_valid_ask_again; lowercased
 *                 typed:  appears (case-folded) in a typed turn, else contact_not_typed_ask_visitor_to_type_it
 *                 spoken: unverified
 * ```
 *
 * A spoken contact is never refused for not matching: speech recognition writes "six nine…" as often as digits, so it
 * is kept and marked unverified (the email says "not confirmed"). Refusal codes are safe to log; a name, a contact or a
 * message never is.
 */
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { idCodec } from "../id/brand.js";
import { isE164 } from "../telephony/sip-trunk/wire-number.js";
/** The id codec of a taken message (UUID v7, minted in the application). */
export const takenMessageIdCodec = idCodec("TakenMessageId");
/** `taken_message.contact_kind`, in the tool's fixed enum order. */
export const CONTACT_KIND = ["calling_number", "phone", "email"];
/** `taken_message.offered_by`: what led to the message. */
export const OFFERED_BY = ["in_conversation", "handoff", "after_hours", "overflow"];
/** `taken_message.notice_state`: moves once from `pending`. */
export const TAKEN_MESSAGE_NOTICE_STATE = ["pending", "sent", "coalesced", "no_recipient", "mail_off"];
/** The outbox topic written with the row: payload `{ account_id, workspace_id, taken_message_id }`. */
export const TAKEN_MESSAGE_RECORDED_TOPIC = "taken_message.recorded";
/** The outbox topic of an hour's coalesced notice: payload `{ account_id, workspace_id, hour }`. */
export const TAKEN_MESSAGE_COALESCE_TOPIC = "taken_message.coalesce";
/**
 * The mail kind of the notice (`MAIL_KIND` since plan-018 NOTE, together with its composer in `packages/mail` and
 * migration 0144's CHECK).
 */
export const TAKEN_MESSAGE_NOTICE_MAIL_KIND = "taken_message_notice";
/** The caller's name, trimmed, at most this many characters (0144's CHECK). */
export const CALLER_NAME_CHARACTER_LIMIT = 120;
/** The message the VoiceAgent collected, trimmed, at most this many characters. */
export const TAKEN_MESSAGE_TEXT_CHARACTER_LIMIT = 2_000;
/** An email address, at most this many characters. */
export const CONTACT_EMAIL_CHARACTER_LIMIT = 254;
/** Every refusal `take_message` answers, as the `why` the model reads. */
export const TAKE_MESSAGE_REFUSAL = [
    "arguments_malformed",
    "read_back_first",
    "name_missing",
    "message_missing",
    "no_calling_number_ask_for_a_number",
    "phone_not_valid_ask_again",
    "contact_not_typed_ask_visitor_to_type_it",
    "email_not_valid_ask_again",
];
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
function refused(why) {
    return Object.freeze({ kind: "refused", why });
}
function digitsOf(text) {
    return text.replace(/\D/gu, "");
}
function isContactKind(value) {
    return typeof value === "string" && CONTACT_KIND.includes(value);
}
function phoneOf(contact, context) {
    const trimmed = contact.trim();
    if (context.region === null && !trimmed.startsWith("+"))
        return { kind: "refused", why: "phone_not_valid_ask_again" };
    const parsed = context.region === null
        ? parsePhoneNumberFromString(trimmed)
        : parsePhoneNumberFromString(trimmed, context.region);
    if (parsed === undefined || !parsed.isValid() || !isE164(parsed.number)) {
        return { kind: "refused", why: "phone_not_valid_ask_again" };
    }
    const national = String(parsed.nationalNumber);
    if (context.mode === "typed") {
        if (!context.typedText.some((turn) => digitsOf(turn).includes(national))) {
            return { kind: "refused", why: "contact_not_typed_ask_visitor_to_type_it" };
        }
        return { kind: "contact", value: parsed.number, verified: true };
    }
    return { kind: "contact", value: parsed.number, verified: digitsOf(context.spokenText.join(" ")).includes(national) };
}
function emailOf(contact, context) {
    const value = contact.trim().toLowerCase();
    if (value.length > CONTACT_EMAIL_CHARACTER_LIMIT || !EMAIL.test(value)) {
        return { kind: "refused", why: "email_not_valid_ask_again" };
    }
    if (context.mode === "typed") {
        if (!context.typedText.some((turn) => turn.toLowerCase().includes(value))) {
            return { kind: "refused", why: "contact_not_typed_ask_visitor_to_type_it" };
        }
        return { kind: "contact", value, verified: true };
    }
    return { kind: "contact", value, verified: false };
}
/**
 * The verdict on one `take_message` call: the model's parsed arguments (`name`, `contact_kind`, `contact`, `message`,
 * `confirmed`) checked against what the conversation knows. Pure; never throws.
 */
export function takeMessageVerdictOf(argument, context) {
    if (typeof argument !== "object" || argument === null || Array.isArray(argument))
        return refused("arguments_malformed");
    const raw = argument;
    const { name, contact_kind: contactKind, contact, message, confirmed } = raw;
    if (typeof name !== "string" || typeof message !== "string" || typeof confirmed !== "boolean" || !isContactKind(contactKind)) {
        return refused("arguments_malformed");
    }
    if (contact !== undefined && contact !== null && typeof contact !== "string")
        return refused("arguments_malformed");
    if (!confirmed)
        return refused("read_back_first");
    const callerName = name.trim();
    if (callerName.length === 0 || callerName.length > CALLER_NAME_CHARACTER_LIMIT)
        return refused("name_missing");
    const messageText = message.trim();
    if (messageText.length === 0 || messageText.length > TAKEN_MESSAGE_TEXT_CHARACTER_LIMIT)
        return refused("message_missing");
    let verdict;
    if (contactKind === "calling_number") {
        if (context.callingNumber === null)
            return refused("no_calling_number_ask_for_a_number");
        verdict = { kind: "contact", value: context.callingNumber, verified: true };
    }
    else if (typeof contact !== "string" || contact.trim() === "") {
        return refused(contactKind === "phone" ? "phone_not_valid_ask_again" : "email_not_valid_ask_again");
    }
    else {
        verdict = contactKind === "phone" ? phoneOf(contact, context) : emailOf(contact, context);
    }
    if (verdict.kind === "refused")
        return refused(verdict.why);
    return Object.freeze({
        kind: "accepted",
        callerName,
        contactKind,
        contactValue: verdict.value,
        contactVerified: verdict.verified,
        messageText,
    });
}
/** The idempotency key of a `take_message` call: one row per conversation, turn and tool-call ordinal. */
export function takeMessageIdempotencyKeyOf(conversationId, turnIndex, toolOrdinal) {
    return `${conversationId}:${String(turnIndex)}:${String(toolOrdinal)}`;
}
/** The idempotency key of an after-hours or busy message (OD-4): one row per conversation start. */
export function unstructuredTakenMessageKeyOf(conversationId, startedAtMs) {
    return `${conversationId}:at:${String(startedAtMs)}`;
}
//# sourceMappingURL=taken-message.js.map